import { sleeperApi } from './sleeperApi';
import { espnApi } from './espnApi';
import { yahooApi } from './yahooApi';
import {
  ApiResponse,
  League,
  Roster,
  Matchup,
  User,
  Player
} from '../types/fantasy';

export type Platform = 'sleeper' | 'espn' | 'yahoo' | 'direct' | 'manual';

export interface LeagueInput {
  platform: Platform;
  leagueId: string;
  season?: number;
  cookies?: string; // For ESPN private leagues
  accessToken?: string; // For Yahoo OAuth
}

export interface FantasyLeagueData {
  league: ApiResponse<League>;
  rosters: ApiResponse<Roster[]>;
  matchups: ApiResponse<Matchup[]>;
  users: ApiResponse<User[]>;
}

class FantasyAPI {
  private validateInput(input: LeagueInput): string | null {
    // Skip validation for direct and manual platforms since no league data is needed
    if (input.platform === 'direct' || input.platform === 'manual') {
      return null;
    }

    if (!input.leagueId) {
      return 'League ID is required';
    }

    if (!['sleeper', 'espn', 'yahoo', 'direct', 'manual'].includes(input.platform)) {
      return 'Platform must be one of: sleeper, espn, yahoo, direct, manual';
    }

    if (input.platform === 'yahoo' && !input.accessToken) {
      return 'Yahoo requires OAuth access token';
    }

    if (input.platform === 'espn' && input.season && (input.season < 2020 || input.season > 2025)) {
      return 'ESPN API supports seasons 2020-2025';
    }

    return null;
  }

  private normalizePlayerData(player: any, platform: Platform): Player {
    switch (platform) {
      case 'sleeper':
        return {
          id: player.player_id || player.id || player,
          name: player.full_name || player.name || 'Unknown Player',
          position: player.position || 'UNK',
          team: player.team || 'UNK'
        };
      
      case 'espn':
        return {
          id: player.id || player.playerId?.toString() || player,
          name: player.name || player.fullName || 'Unknown Player',
          position: player.position || 'UNK',
          team: player.team || 'UNK',
          points: player.points,
          projectedPoints: player.projectedPoints
        };
      
      case 'yahoo':
        return {
          id: player.player_id?.[0] || player.id || player,
          name: player.name?.[0]?.full?.[0] || player.name || 'Unknown Player',
          position: player.display_position?.[0] || player.position || 'UNK',
          team: player.editorial_team_abbr?.[0] || player.team || 'UNK'
        };
      
      default:
        return {
          id: player.id || player,
          name: player.name || 'Unknown Player',
          position: player.position || 'UNK',
          team: player.team || 'UNK'
        };
    }
  }

  async getLeagueData(input: LeagueInput, week?: number): Promise<FantasyLeagueData> {
    const validationError = this.validateInput(input);
    if (validationError) {
      const errorResponse: ApiResponse<any> = {
        success: false,
        error: validationError,
        platform: input.platform
      };
      
      return {
        league: errorResponse,
        rosters: errorResponse,
        matchups: errorResponse,
        users: errorResponse
      };
    }

    const currentSeason = input.season || new Date().getFullYear();
    const currentWeek = week || 1;

    try {
      switch (input.platform) {
        case 'sleeper':
          return await this.getSleeperData(input.leagueId, currentWeek);
        
        case 'espn':
          return await this.getESPNData(input.leagueId, currentSeason, currentWeek, input.cookies);
        
        case 'yahoo':
          if (input.accessToken) {
            yahooApi.setAccessToken(input.accessToken);
          }
          return await this.getYahooData(input.leagueId, currentWeek);
        
        case 'direct':
          // Return empty data for direct talk option
          const directSuccessResponse: ApiResponse<any> = {
            success: true,
            data: {},
            platform: 'direct'
          };
          
          return {
            league: directSuccessResponse,
            rosters: directSuccessResponse,
            matchups: directSuccessResponse,
            users: directSuccessResponse
          };

        case 'manual':
          // Return empty data for manual entry option
          const manualSuccessResponse: ApiResponse<any> = {
            success: true,
            data: {},
            platform: 'manual'
          };
          
          return {
            league: manualSuccessResponse,
            rosters: manualSuccessResponse,
            matchups: manualSuccessResponse,
            users: manualSuccessResponse
          };
        
        default:
          const errorResponse: ApiResponse<any> = {
            success: false,
            error: 'Unsupported platform',
            platform: input.platform
          };
          
          return {
            league: errorResponse,
            rosters: errorResponse,
            matchups: errorResponse,
            users: errorResponse
          };
      }
    } catch (error) {
      const errorResponse: ApiResponse<any> = {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        platform: input.platform
      };
      
      return {
        league: errorResponse,
        rosters: errorResponse,
        matchups: errorResponse,
        users: errorResponse
      };
    }
  }

  private async getSleeperData(leagueId: string, week: number): Promise<FantasyLeagueData> {
    const [league, rosters, matchups, users] = await Promise.all([
      sleeperApi.getLeague(leagueId),
      sleeperApi.getLeagueRosters(leagueId),
      sleeperApi.getMatchups(leagueId, week),
      sleeperApi.getLeagueUsers(leagueId)
    ]);

    return { league, rosters, matchups, users };
  }

  private async getESPNData(leagueId: string, season: number, week: number, cookies?: string): Promise<FantasyLeagueData> {
    const [league, rosters, matchups, users] = await Promise.all([
      espnApi.getLeague(leagueId, season, cookies),
      espnApi.getLeagueRosters(leagueId, season, week, cookies),
      espnApi.getMatchups(leagueId, season, week, cookies),
      espnApi.getLeagueUsers(leagueId, season, cookies)
    ]);

    return { league, rosters, matchups, users };
  }

  private async getYahooData(leagueKey: string, week: number): Promise<FantasyLeagueData> {
    const [league, rosters] = await Promise.all([
      yahooApi.getLeague(leagueKey),
      yahooApi.getLeagueRosters(leagueKey, week)
    ]);

    const users = await yahooApi.getLeagueTeams(leagueKey);
    const matchups = await yahooApi.getMatchups(leagueKey, week);

    return { league, rosters, matchups, users };
  }

  // Utility methods for common tasks
  async getLeagueInfo(input: LeagueInput): Promise<ApiResponse<League>> {
    const data = await this.getLeagueData(input);
    return data.league;
  }

  async getRosters(input: LeagueInput, week?: number): Promise<ApiResponse<Roster[]>> {
    const data = await this.getLeagueData(input, week);
    return data.rosters;
  }

  async getMatchups(input: LeagueInput, week: number): Promise<ApiResponse<Matchup[]>> {
    const data = await this.getLeagueData(input, week);
    return data.matchups;
  }

  async getUsers(input: LeagueInput): Promise<ApiResponse<User[]>> {
    const data = await this.getLeagueData(input);
    return data.users;
  }

  // Helper method to get current week for a league
  async getCurrentWeek(input: LeagueInput): Promise<number> {
    const leagueResponse = await this.getLeagueInfo(input);
    return leagueResponse.success && leagueResponse.data ? leagueResponse.data.currentWeek : 1;
  }

  // Helper method to check if league is accessible
  async validateLeague(input: LeagueInput): Promise<{ isValid: boolean; error?: string }> {
    const leagueResponse = await this.getLeagueInfo(input);
    
    if (!leagueResponse.success) {
      return { 
        isValid: false, 
        error: leagueResponse.error || 'Unable to access league' 
      };
    }

    return { isValid: true };
  }

  // Get supported platforms info
  getSupportedPlatforms(): Array<{platform: Platform, name: string, requiresAuth: boolean, authType?: string}> {
    return [
      {
        platform: 'sleeper',
        name: 'Sleeper',
        requiresAuth: false
      },
      {
        platform: 'espn',
        name: 'ESPN',
        requiresAuth: false, // Public leagues don't require auth
        authType: 'cookies' // Private leagues need espn_s2 and SWID cookies
      },
      {
        platform: 'yahoo',
        name: 'Yahoo Fantasy',
        requiresAuth: true,
        authType: 'oauth' // Requires OAuth access token
      }
    ];
  }

  // Format league ID helper
  formatLeagueId(platform: Platform, leagueId: string, season?: number): string {
    switch (platform) {
      case 'yahoo':
        // Yahoo uses game_key.l.league_id format
        // Game keys: 415 (2025), 414 (2024), 413 (2023), etc.
        const currentYear = new Date().getFullYear();
        const currentGameKey = 415 - (2025 - currentYear); // Calculate current year game key
        const gameKey = season ? (415 - (2025 - season)) : currentGameKey;
        return leagueId.includes('.l.') ? leagueId : `${gameKey}.l.${leagueId}`;
      
      case 'espn':
      case 'sleeper':
      default:
        return leagueId;
    }
  }
}

export const fantasyApi = new FantasyAPI();